import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getUserDetails, updateUserProfile, changePassword, updateBazosVouchers } from '../api/user';
import { t } from '../i18n';

// Accounts that manage Bazos poukazka (voucher) codes
const VOUCHER_USERS = ['lerent', 'autojokr'];

// Codes shown per group before the list has to be expanded
const VOUCHER_PREVIEW_COUNT = 5;

const PageTitle = styled.h1`
  font-size: 32px;
  margin-bottom: 30px;
`;

const InfoContainer = styled.div`
  max-width: 600px;
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const InfoGroup = styled.div`
  margin-bottom: 25px;
`;

const EditableInfoGroup = styled.div`
  margin-bottom: 25px;
  display: flex;
  align-items: flex-end;
  gap: 15px;
`;

const Label = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: #555;
  font-size: 14px;
`;

const Value = styled.div`
  font-size: 16px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #eee;
`;

const Input = styled.input`
  font-size: 16px;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  flex: 1;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }
`;

const SaveButton = styled.button`
  background-color: #2196f3;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #1976d2;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  &:active {
    background-color: #1565c0;
  }
`;

const StatusMessage = styled.div<{ isError?: boolean }>`
  margin-top: 20px;
  padding: 10px;
  border-radius: 4px;
  text-align: center;
  background-color: ${props => props.isError ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.isError ? '#c62828' : '#2e7d32'};
`;

const Textarea = styled.textarea`
  font-size: 15px;
  font-family: monospace;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  width: 100%;
  min-height: 140px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
  }
`;

const FieldHint = styled.div`
  font-size: 13px;
  color: #777;
  margin: 8px 0 12px;
`;

const VoucherSummary = styled.div<{ $level: 'ok' | 'low' | 'empty' }>`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 16px 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.$level === 'empty' ? '#c62828' : (props.$level === 'low' ? '#ef6c00' : '#2e7d32')};

  span.count {
    font-size: 22px;
    line-height: 1;
  }
`;

const VoucherGroupLabel = styled.div`
  font-size: 13px;
  color: #777;
  margin: 14px 0 6px;
`;

const VoucherChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 150px;
  overflow-y: auto;
  padding: 2px;
`;

const VoucherChip = styled.span<{ $used?: boolean }>`
  font-family: monospace;
  font-size: 13px;
  padding: 3px 9px;
  border-radius: 12px;
  border: 1px solid ${props => props.$used ? '#e0e0e0' : '#2e7d32'};
  background-color: ${props => props.$used ? '#f5f5f5' : '#e8f5e9'};
  color: ${props => props.$used ? '#9e9e9e' : '#1b5e20'};
  text-decoration: ${props => props.$used ? 'line-through' : 'none'};
  font-weight: ${props => props.$used ? 400 : 600};
`;

const VoucherToggle = styled.button`
  background: none;
  border: none;
  padding: 4px 0;
  margin-top: 4px;
  font-size: 13px;
  color: #1976d2;
  cursor: pointer;
  text-decoration: underline;

  &:hover { color: #0d47a1; }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  font-size: 18px;
  color: #666;
`;

const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // User fields
  const [username, setUsername] = useState('');
  const [bzs_username, setBzsUsername] = useState('');
  const [editableBzsUsername, setEditableBzsUsername] = useState('');
  const [phone_number, setPhoneNumber] = useState('');
  const [country, setCountry] = useState('');

  // Bazos voucher codes (one per line)
  const [vouchersText, setVouchersText] = useState('');
  const [savedVouchers, setSavedVouchers] = useState<string[]>([]);
  const [usedVoucherCodes, setUsedVoucherCodes] = useState<string[]>([]);
  const [showAllUnused, setShowAllUnused] = useState(false);
  const [showAllUsed, setShowAllUsed] = useState(false);
  const [vouchersSaving, setVouchersSaving] = useState(false);
  const [vouchersError, setVouchersError] = useState('');
  const [vouchersSuccess, setVouchersSuccess] = useState('');

  // Password change fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get logged in user from localStorage
        const storedUser = localStorage.getItem('user');
        if (!storedUser) {
          setError('Nie ste prihlásený');
          setLoading(false);
          return;
        }
        
        const { username } = JSON.parse(storedUser);
        
        // Fetch user details from server
        const response = await getUserDetails(username);
        
        if (!response.success) {
          setError(response.message || 'Nepodarilo sa načítať údaje');
          setLoading(false);
          return;
        }
        
        // Set user data
        setUsername(response.user.username || '');
        setBzsUsername(response.user.bzs_username || '');
        setEditableBzsUsername(response.user.bzs_username || '');
        setPhoneNumber(response.user.phone_number || '');
        setCountry(response.user.country || '');

        const vouchers: string[] = response.user.bazos_vouchers || [];
        setSavedVouchers(vouchers);
        // The box is for adding codes - the saved pool is shown in the groups
        // below, so it starts empty rather than mirroring the list
        setVouchersText('');
        setUsedVoucherCodes(response.user.bazos_vouchers_used || []);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Chyba pri načítaní údajov');
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, []);

  const handleSaveBzsUsername = async () => {
    if (!editableBzsUsername.trim()) {
      setError('BZS používateľské meno nemôže byť prázdne');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const response = await updateUserProfile(username, editableBzsUsername.trim());
      
      if (!response.success) {
        setError(response.message || 'Nepodarilo sa uložiť zmeny');
        return;
      }

      // Update the current bzs_username state
      setBzsUsername(editableBzsUsername.trim());
      setSuccessMessage('BZS používateľské meno bolo úspešne aktualizované');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      console.error('Error saving bzs username:', err);
      setError('Chyba pri ukladaní zmien');
    } finally {
      setSaving(false);
    }
  };

  // Accepts codes separated by new lines, commas, semicolons or spaces
  const parseVouchers = (text: string): string[] => {
    const seen = new Set<string>();
    const codes: string[] = [];
    text.split(/[\s,;]+/).forEach(part => {
      const code = part.trim().toUpperCase();
      if (code && !seen.has(code)) {
        seen.add(code);
        codes.push(code);
      }
    });
    return codes;
  };

  // A code is spent iff it appears in the consumed history, whatever status that
  // entry carries - both "used" and "invalid" mean never offer it again. The
  // history is append-only and not a subset of the pool (rotated-out batches stay
  // in it), so availability is this set difference, never a count comparison.
  const usedVoucherSet = new Set(usedVoucherCodes);
  const unusedVouchers = savedVouchers.filter(code => !usedVoucherSet.has(code));
  const usedInPool = savedVouchers.filter(code => usedVoucherSet.has(code));
  const voucherLevel: 'ok' | 'low' | 'empty' =
    unusedVouchers.length === 0 ? 'empty' : (unusedVouchers.length <= 5 ? 'low' : 'ok');

  const parsedVouchers = parseVouchers(vouchersText);
  const invalidVouchers = parsedVouchers.filter(code => !/^[A-Z0-9]{3,20}$/.test(code));
  // The box adds to the pool. Anything already saved is ignored rather than
  // re-added, and the pool is never replaced by whatever happens to be typed -
  // otherwise saving a handful of new codes would wipe the rest.
  const newVouchers = parsedVouchers.filter(code => !savedVouchers.includes(code));
  const duplicateCount = parsedVouchers.length - newVouchers.length;
  const canSaveVouchers = parsedVouchers.length > 0 && invalidVouchers.length === 0;

  const handleSaveVouchers = async () => {
    setVouchersError('');
    setVouchersSuccess('');

    if (invalidVouchers.length > 0) {
      setVouchersError('Neplatné kódy: ' + invalidVouchers.slice(0, 5).join(', '));
      return;
    }

    try {
      setVouchersSaving(true);

      if (newVouchers.length === 0) {
        setVouchersText('');
        setVouchersSuccess('Tieto kódy už v zozname sú');
        setTimeout(() => setVouchersSuccess(''), 3000);
        return;
      }

      const merged = [...savedVouchers, ...newVouchers];
      const response = await updateBazosVouchers(username, merged);

      if (!response.success) {
        setVouchersError(response.message || 'Nepodarilo sa uložiť kódy');
        return;
      }

      const saved = response.vouchers || merged;
      setSavedVouchers(saved);
      // Emptied so the groups below are the single view of what is saved
      setVouchersText('');
      setVouchersSuccess(t('Pridané kódy: {count}', { count: newVouchers.length }));

      setTimeout(() => setVouchersSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving vouchers:', err);
      setVouchersError('Chyba pri ukladaní kódov');
    } finally {
      setVouchersSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Vyplňte všetky polia hesla');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Nové heslo musí mať aspoň 8 znakov');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Heslá sa nezhodujú');
      return;
    }

    try {
      setPasswordSaving(true);

      const response = await changePassword(username, currentPassword, newPassword);

      if (!response.success) {
        if (response.message === 'Current password is incorrect') {
          setPasswordError('Aktuálne heslo je nesprávne');
        } else {
          setPasswordError('Chyba pri zmene hesla');
        }
        return;
      }

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSuccess('Heslo bolo úspešne zmenené');

      setTimeout(() => {
        setPasswordSuccess('');
      }, 3000);

    } catch (err) {
      console.error('Error changing password:', err);
      setPasswordError('Chyba pri zmene hesla');
    } finally {
      setPasswordSaving(false);
    }
  };

  const hasChanges = editableBzsUsername.trim() !== bzs_username;

  if (loading) {
    return (
      <div>
        <PageTitle>{t('Nastavenia používateľa')}</PageTitle>
        <LoadingContainer>{t('Načítavam údaje používateľa...')}</LoadingContainer>
      </div>
    );
  }

  return (
    <div>
      <PageTitle>{t('Nastavenia používateľa')}</PageTitle>
      
      <InfoContainer>
        {error && <StatusMessage isError>{t(error)}</StatusMessage>}
        {successMessage && <StatusMessage>{t(successMessage)}</StatusMessage>}
        
        <InfoGroup>
          <Label>{t('Používateľské meno')}</Label>
          <Value>{username}</Value>
        </InfoGroup>
        
        <EditableInfoGroup>
          <div style={{ flex: 1 }}>
          <Label>{t('Používateľské meno BZS')}</Label>
            <Input
              type="text"
              value={editableBzsUsername}
              onChange={(e) => setEditableBzsUsername(e.target.value)}
              placeholder={t('Zadajte BZS používateľské meno')}
              disabled={saving}
            />
          </div>
          <SaveButton
            onClick={handleSaveBzsUsername}
            disabled={saving || !hasChanges}
          >
            {saving ? t('Ukladám...') : t('Uložiť')}
          </SaveButton>
        </EditableInfoGroup>
        
        <InfoGroup>
          <Label>{t('Telefónne číslo')}</Label>
          <Value>{phone_number || t('Nie je zadané')}</Value>
        </InfoGroup>

        <InfoGroup>
          <Label>{t('Krajina')}</Label>
          <Value>{country}</Value>
        </InfoGroup>

        {VOUCHER_USERS.includes(username) && (
          <InfoGroup style={{ borderTop: '1px solid #eee', paddingTop: '25px' }}>
            <Label style={{ fontSize: '16px', marginBottom: '15px' }}>{t('Poukážky Bazoš')}</Label>

            {vouchersError && <StatusMessage isError>{t(vouchersError)}</StatusMessage>}
            {vouchersSuccess && <StatusMessage>{t(vouchersSuccess)}</StatusMessage>}

            {savedVouchers.length > 0 && (
              <VoucherSummary $level={voucherLevel}>
                <span className="count">{unusedVouchers.length}</span>
                <span>
                  {t('nepoužitých z')} {savedVouchers.length}
                  {voucherLevel === 'empty' && ` — ${t('pridajte nové kódy')}`}
                  {voucherLevel === 'low' && ` — ${t('čoskoro dôjdu')}`}
                </span>
              </VoucherSummary>
            )}

            <Textarea
              value={vouchersText}
              onChange={(e) => setVouchersText(e.target.value)}
              placeholder={'AAAAA\nBBBBBB'}
              disabled={vouchersSaving}
              spellCheck={false}
            />
            <FieldHint>
              {parsedVouchers.length === 0
                ? `${t('Jeden kód na riadok')} — ${t('pridajú sa k uloženým')} (${savedVouchers.length})`
                : `${t('Pridá sa')}: ${newVouchers.length}${duplicateCount > 0 ? ` — ${duplicateCount} ${t('už v zozname')}` : ''}`}
            </FieldHint>

            <SaveButton
              onClick={handleSaveVouchers}
              disabled={vouchersSaving || !canSaveVouchers}
            >
              {vouchersSaving ? t('Ukladám...') : t('Uložiť kódy')}
            </SaveButton>

            {unusedVouchers.length > 0 && (
              <>
                <VoucherGroupLabel>{t('Nepoužité')} ({unusedVouchers.length})</VoucherGroupLabel>
                <VoucherChips>
                  {(showAllUnused ? unusedVouchers : unusedVouchers.slice(0, VOUCHER_PREVIEW_COUNT))
                    .map(code => <VoucherChip key={code}>{code}</VoucherChip>)}
                </VoucherChips>
                {unusedVouchers.length > VOUCHER_PREVIEW_COUNT && (
                  <VoucherToggle onClick={() => setShowAllUnused(!showAllUnused)}>
                    {showAllUnused ? t('Zobraziť menej') : `${t('Zobraziť všetky')} (${unusedVouchers.length})`}
                  </VoucherToggle>
                )}
              </>
            )}

            {usedInPool.length > 0 && (
              <>
                <VoucherGroupLabel>{t('Použité')} ({usedInPool.length})</VoucherGroupLabel>
                <VoucherChips>
                  {(showAllUsed ? usedInPool : usedInPool.slice(0, VOUCHER_PREVIEW_COUNT))
                    .map(code => <VoucherChip key={code} $used>{code}</VoucherChip>)}
                </VoucherChips>
                {usedInPool.length > VOUCHER_PREVIEW_COUNT && (
                  <VoucherToggle onClick={() => setShowAllUsed(!showAllUsed)}>
                    {showAllUsed ? t('Zobraziť menej') : `${t('Zobraziť všetky')} (${usedInPool.length})`}
                  </VoucherToggle>
                )}
              </>
            )}
          </InfoGroup>
        )}

        <InfoGroup style={{ borderTop: '1px solid #eee', paddingTop: '25px' }}>
          <Label style={{ fontSize: '16px', marginBottom: '15px' }}>{t('Zmena hesla')}</Label>

          {passwordError && <StatusMessage isError>{t(passwordError)}</StatusMessage>}
          {passwordSuccess && <StatusMessage>{t(passwordSuccess)}</StatusMessage>}

          <div style={{ marginBottom: '15px' }}>
            <Label>{t('Aktuálne heslo')}</Label>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={passwordSaving}
              autoComplete="current-password"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <Label>{t('Nové heslo')}</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={passwordSaving}
              autoComplete="new-password"
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <Label>{t('Potvrďte nové heslo')}</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={passwordSaving}
              autoComplete="new-password"
              style={{ width: '100%' }}
            />
          </div>

          <SaveButton
            onClick={handleChangePassword}
            disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
          >
            {passwordSaving ? t('Ukladám...') : t('Zmeniť heslo')}
          </SaveButton>
        </InfoGroup>
      </InfoContainer>
    </div>
  );
};

export default SettingsPage; 